/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import { EuiSpacer, EuiHorizontalRule, EuiTitle, EuiText } from '@elastic/eui'

import type { CSP } from '@modules/ui-types/providers'
import { getProviderIdFromRegionId } from '@modules/cloud-lib/providers'
import { FormattedMessage } from '@modules/i18n'

import NameField from './fields/NameField'
import DescriptionField from './fields/DescriptionField'
import CloudProviderField from './fields/CloudProviderField'
import DefaultVpceFilterField from './fields/VpceFilterField/Default'
import AzureVpceFilterField from './fields/VpceFilterField/Azure'
import RemoteClusterField from './fields/RemoteClusterField'
import DeploymentAssociationsField from './fields/AssociationsField/DeploymentAssociationsField'
import IncludeByDefaultField from './fields/IncludeByDefaultField'
import ConnectivityField from './fields/ConnectivityField'
import HostedRegionField from './fields/RegionField/HostedRegionField'
import ServerlessRegionField from './fields/RegionField/ServerlessRegionField'

import type { TrafficFilterHandlerChildProps } from './types'

const PrivateConnectionForm: React.FunctionComponent<TrafficFilterHandlerChildProps> = ({
  formMode,
  resourceType,
  name,
  setName,
  description,
  setDescription,
  includeByDefault,
  setIncludeByDefault,
  type,
  setType,
  region,
  setRegion,
  rules,
  setRules,
  associations,
  setAssociations,
}) => {
  const [cloudProvider, setCloudProvider] = useState<CSP | ''>(
    // Start blank so that the CloudProviderField will set the default,
    // chanining the change to the region field which will set *its* default.
    formMode === 'create' ? '' : getProviderIdFromRegionId(region),
  )

  return (
    <Fragment>
      <CloudProviderField
        value={cloudProvider}
        onChange={setCloudProvider}
        isDisabled={formMode === 'update'}
      />
      <EuiSpacer size='m' />
      {resourceType === 'hosted' && (
        <HostedRegionField
          value={region}
          onChange={formMode === 'create' ? setRegion : undefined}
          csp={cloudProvider}
          isDisabled={formMode === 'update'}
        />
      )}
      {resourceType === 'serverless' && (
        <ServerlessRegionField
          value={region}
          onChange={formMode === 'create' ? setRegion : undefined}
          csp={cloudProvider}
          isDisabled={formMode === 'update'}
        />
      )}
      <EuiSpacer size='m' />
      <ConnectivityField
        value={type}
        onChange={(value) => {
          setType?.(value)
          setRules([])
        }}
        csp={cloudProvider}
      />
      <EuiSpacer size='m' />
      <NameField value={name} onChange={setName} />
      <EuiSpacer size='m' />
      <DescriptionField value={description || ''} onChange={setDescription} />
      <EuiSpacer size='m' />
      {(type === 'vpce' || type === 'gcp_private_service_connect_endpoint') && (
        <DefaultVpceFilterField rules={rules} onChange={setRules} />
      )}
      {type === 'azure_private_endpoint' && (
        <AzureVpceFilterField rules={rules} onChange={setRules} />
      )}
      {type === 'remote_cluster' && <RemoteClusterField rules={rules} onChange={setRules} />}

      <EuiHorizontalRule />

      <EuiTitle size='xs'>
        <h3>
          <FormattedMessage
            id='privateConnectionForm.associationsField.title'
            defaultMessage='Apply to resources <small>(optional)</small>'
            values={{
              small: (content) => (
                <EuiText size='xs' component='span'>
                  <strong>{content}</strong>
                </EuiText>
              ),
            }}
          />
        </h3>
      </EuiTitle>
      <EuiSpacer size='m' />

      {resourceType === 'hosted' && (
        <DeploymentAssociationsField
          regionId={region}
          value={associations}
          onChange={setAssociations}
        />
      )}
      <EuiSpacer size='m' />
      <IncludeByDefaultField value={includeByDefault || false} onChange={setIncludeByDefault} />
    </Fragment>
  )
}

export default PrivateConnectionForm
